import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../../../../api";
import Topbar from "../../Tools/Topbar";
import LeftSidebar from "../../Tools/LeftSidebar";
import Settings from "../../Tools/Setting/Settings";
import useSettings from "../../Tools/Setting/useSettings";
import PatientSituationModal from "../EMSCasesTab/CaseDetails/PatientSituationModal";
import PatientSituationtransfercaseModal from "../TransferCasesTab/TransferCaseModal/PatientSituationtransfercase";
import TransferCaseModal from "../TransferCasesTab/TransferbuttonModal/Transfermodal";
import { format } from "date-fns";

// Fetch and cache date/time format settings
const DateTimeFormatFetcher = () => {
  useEffect(() => {
    const fetchFormats = async () => {
      try {
        const { data } = await axiosInstance.get("/ems/get-system-setting", {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('authToken')}`,
          },
        });

        if (data?.status === "success" && data.data.length > 0) {
          const settings = data.data[0];
          sessionStorage.setItem("date_format", settings.date_format || "DD/MM/YYYY");
          sessionStorage.setItem("time_format", settings.time_format || "24");
        }
      } catch (err) {
        console.error("Failed to load date/time format", err);
        sessionStorage.setItem("date_format", "DD/MM/YYYY");
        sessionStorage.setItem("time_format", "24");
      }
    };
    fetchFormats();
  }, []);
  return null;
};
const formatCaseDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
      const timeFormat = sessionStorage.getItem('time_format') || '24';

      const dateFormatMap = {
        'DD/MM/YYYY': 'dd/MM/yyyy',
        'MM/DD/YYYY': 'MM/dd/yyyy',
        'YYYY-MM-DD': 'yyyy-MM-dd',
      };

      const timeFormatString = timeFormat === '12' ? 'hh:mm:ss a' : 'HH:mm:ss';
      const formatString = `${dateFormatMap[dateFormat] || 'dd/MM/yyyy'} ${timeFormatString}`;

      let utcDate;
      if (dateString.includes('T')) {
        utcDate = new Date(dateString);
      } else {
        utcDate = new Date(dateString.replace(' ', 'T') + 'Z');
      }

      const utcTimestamp = new Date(
        utcDate.getUTCFullYear(),
        utcDate.getUTCMonth(),
        utcDate.getUTCDate(),
        utcDate.getUTCHours(),
        utcDate.getUTCMinutes(),
        utcDate.getUTCSeconds()
      );

      return format(utcTimestamp, formatString);
    } catch (err) {
      console.error('Error formatting date:', err, dateString);
      return 'Invalid Date';
    }
  };

const EMSClosedDetailTab = () => {
  // State
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [caseId] = useState(() => sessionStorage.getItem("currentCaseId"));
  const [caseData, setCaseData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canTransfer, setCanTransfer] = useState(false);

  // Modals
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [showTransferPatientModal, setShowTransferPatientModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const patientModalRef = useRef(null);

  const UserId = sessionStorage.getItem("user_id") || sessionStorage.getItem("UserId");
  const caseStatus = sessionStorage.getItem("ClosedTabCaseStatus");

  const { settings, updateSetting, handleColorTheme } = useSettings();
  const settingsRef = useRef(null);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  // Date formatting utility
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const df = sessionStorage.getItem("date_format") || "DD/MM/YYYY";
      const tf = sessionStorage.getItem("time_format") || "24";

      const dateMap = {
        "DD/MM/YYYY": "dd/MM/yyyy",
        "MM/DD/YYYY": "MM/dd/yyyy",
        "YYYY-MM-DD": "yyyy-MM-dd",
      };

      const timePart = tf === "12" ? " hh:mm:ss a" : " HH:mm:ss";
      const formatStr = `${dateMap[df] || "dd/MM/yyyy"}${timePart}`;

      const date = new Date(dateStr.includes("T") ? dateStr : dateStr.replace(" ", "T") + "Z");
      return format(date, formatStr);
    } catch {
      return "Invalid Date";
    }
  };

  // Extract fetchCase so we can call it anytime
  const fetchCase = async () => {
    if (!caseId) {
      setError("No case selected");
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get(`/ems/cases/${caseId}`, {
        headers: {
          Accept: "application/vnd.api+json",
          "Content-Type": "application/vnd.api+json",
          Authorization: `Bearer ${sessionStorage.getItem('authToken')}`,
        },
      });
      const payload = Array.isArray(data) ? data[0] : data;
      setCaseData(payload);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load case details");
    } finally {
      setIsLoading(false);
    }
  };

  // Run on mount
  useEffect(() => {
    fetchCase();
  }, [caseId]);


  // Check Transfer Button Permission
  useEffect(() => {
    const checkTransfer = async () => {
      if (!caseId || !UserId) return;
      try {
        const { data } = await axiosInstance.get(`/ems/transfer-button-display/${caseId}/${UserId}`);
        setCanTransfer(data.Status === "true");
      } catch (err) {
        console.error("Transfer permission check failed", err);
        setCanTransfer(false);
      }
    };
    checkTransfer();
  }, [caseId, UserId]);

  // Handlers - UPDATED PATIENT SITUATION LOGIC
  const handleTransfer = () => setShowTransferModal(true);

  const openPatientSituation = () => {
    sessionStorage.setItem("currentCaseId", caseId);
    
    console.log("Opening Patient Situation:", {
      caseStatus,
      transferStatus: caseData?.transfer_status,
      caseData
    });

    // Simplified logic based on caseStatus
    if (caseStatus === "2") {
      // Transfer cases - show transfer patient modal
      setShowTransferPatientModal(true);
    } else {
      // Regular cases (status 1) - show regular patient modal
      setIsPatientModalOpen(true);
      setShowPatientModal(true);
    }
  };

  const handleViewPatientSituation = () => {
    sessionStorage.setItem('currentCaseId', caseId);
    setShowPatientModal(true);
    setIsPatientModalOpen(true);
  };

  const handleViewTransferPatientSituation = () => {
    sessionStorage.setItem('currentCaseId', caseId);
    setShowTransferPatientModal(true);
  };

  const openTransferPatientSituation = () => {
    sessionStorage.setItem("currentCaseId", caseId);
    setShowTransferPatientModal(true);
  };

  const openDocument = (url) => {
    setSelectedDocument(url);
    setShowDocumentModal(true);
  };

  // Extract documents
  const documents = caseData
    ? [caseData.documents1, caseData.documents2, caseData.documents3]
        .filter(Boolean)
        .filter((d) => d.trim() !== "")
    : [];

  // Render document in modal
  const renderDocument = (url) => {
    const ext = url.split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
      return <img src={url} alt="Document" className="img-fluid" style={{ maxHeight: "80vh" }} />;
    }
    if (["pdf", "xml"].includes(ext)) {
      return <iframe src={url} width="100%" height="600" title="Document" />;
    }
    return <p>Preview not available for .{ext} files</p>;
  };

  // Transfer button visibility
  const showTransferButton =
    ["EMS Resource Manager", "Admin", "EMS Requester", "Hospital Representative (Nurse)"].includes(
      sessionStorage.getItem("Role_Name")
    ) &&
    caseData?.transfer_status == 0 &&
    canTransfer;

  return (
    <div id="main-wrapper">
      <DateTimeFormatFetcher />

      <LeftSidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div className={`page-wrapper ${isSidebarOpen ? "" : "sidebar-closed"}`}>
        <Topbar toggleSidebar={toggleSidebar} />

        <div className="body-wrapper">
          <div className="container-fluid">
            <Settings
              ref={settingsRef}
              settings={settings}
              updateSetting={updateSetting}
              handleColorTheme={handleColorTheme}
            />

            {/* Header Card */}
            <div className="card w-100 position-relative overflow-hidden">
              <div className="px-4 py-3 border-bottom">
                <h5 className="card-title fw-semibold mb-0 lh-sm">Closed Case Detail</h5>
                <p className="card-subtitle mb-0 mt-1">
                  Comprehensive case overview including patient, EMS, and medical facility details
                </p>
              </div>

              {/* Loading */}
              {isLoading && (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              {/* Error */}
              {error && <div className="alert alert-danger mx-4 mt-4">{error}</div>}

              {/* Content */}
              {!isLoading && !error && caseData && (
                <div className="card-body p-4">
                  <div className="table-responsive rounded-2 mb-4">
                    <table className="table text-nowrap customize-table mb-0 align-middle">
                      <thead className="text-dark fs-4">
                        <tr>
                          <th>
                            <h6 className="fw-semibold mb-0">Field</h6>
                          </th>
                          <th>
                            <h6 className="fw-semibold mb-0">Value</h6>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { label: "Case ID", value: caseData.id },
                          { label: "Name", value: caseData.case_name },
                          { label: "Location", value: caseData.location?.location_name || "N/A" },
                          { label: "Ambulance", value: caseData.ambulance?.plate_number || "N/A" },
                          { label: "Medical Center", value: caseData.medical_center?.name || "N/A" },
                             ...(caseStatus === "2"
                            ? [
                                { label: "Clinic No", value: caseData.patient_situation?.clinic_no || "N/A" },
                                { label: "Request Details", value: caseData.patient_situation?.request_details || "N/A" },
                                { label: "Escort Team(Physician)", value: caseData.patient_situation?.escort_team?.escort_physician || "N/A" },
                                { label: "Escort Team(Nurse)", value: caseData.patient_situation?.escort_team?.escort_nurse || "N/A" },
                                { label: "Escort Team(Paramedic)", value: caseData.patient_situation?.escort_team?.escort_paramedic || "N/A" },
                                { label: "Escort Team(Other)", value: caseData.patient_situation?.escort_team?.other || "N/A" },
                              ]
                            : []),
                          { label: "Paramedics ID", value: caseData.ems_member || "N/A" },
                          { label: "Paramedics Name", value: caseData.ems_member_name || "N/A" },
                          {
                            label: "Paramedics Status",
                            value: caseData.assigned_employee?.available_status === 0 ? "Available" : "Busy",
                          },
                          { label: "Gender", value: caseData.gender || "N/A" },
                          { label: "Age", value: caseData.age || "N/A" },
                          { label: "Bracelet ID", value: caseData.bracelet_id || "N/A" },
                          { label: "Type of Injury", value: caseData.injury_type || "N/A" },
                          { label: "Contact Number", value: caseData.contact_number || "N/A" },
                          { label: "Triage Card", value: caseData.triage_card || "N/A" },
                          { label: "Acknowledgment", value: caseData.acknowledgment || "Pending" },
                          { label: "Created On", value: formatCaseDate(caseData.created_at) },
                          { label: "Closed On", value: formatCaseDate(caseData.closed_at) },

                        ].map((item, idx) => (
                          <tr key={idx}>
                            <td>
                              <p className="mb-0 fw-semibold">{item.label}</p>
                            </td>
                            <td>
                              <p className="mb-0">{item.value}</p>
                            </td>
                          </tr>
                        ))}

                        {/* Conditional Patient Location & Area */}
                        {caseStatus === "1" && (
                          <>
                            <tr>
                              <td>
                                <p className="mb-0 fw-semibold">Patient Location</p>
                              </td>
                              <td>
                                <p className="mb-0">{caseData.p_location?.location_name || "N/A"}</p>
                              </td>
                            </tr>
                            <tr>
                              <td>
                                <p className="mb-0 fw-semibold">Patient Area</p>
                              </td>
                              <td>
                                <p className="mb-0">{caseData.p_area?.area_name || "N/A"}</p>
                              </td>
                            </tr>
                          </>
                        )}

                        {/* Documents */}
                        <tr>
                          <td>
                            <p className="mb-0 fw-semibold">Documents</p>
                          </td>
                          <td>
                            <p className="mb-0 fw-normal">
                              <i className="ti ti-paperclip me-2"></i>
                              {documents.length > 0 ? (
                                documents.map((doc, index) => (
                                  <button
                                    key={index}
                                    type="button"
                                    className="btn btn-link me-3 p-0"
                                    onClick={() => openDocument(doc)}
                                    title={`Document ${index + 1}`}
                                  >
                                    <i className="ti ti-library-photo"></i>
                                  </button>
                                ))
                              ) : (
                                'No documents'
                              )}
                            </p>
                          </td>
                        </tr>

                        {/* Patient Situation - SIMPLIFIED LOGIC */}
                        {caseStatus === '1' && (
                          <tr>
                            <td><p className="mb-0 fw-normal fw-semibold mb-0">Patient Situation</p></td>
                            <td>
                              <p className="mb-0 fw-normal">
                                <a
                                  data-bs-toggle="modal"
                                  data-bs-target="#bs-fullscreen"
                                  className="text-primary me-3"
                                  onClick={handleViewPatientSituation}
                                  style={{ cursor: 'pointer' }}
                                >
                                  Click to view details
                                </a>
                              </p>
                            </td>
                          </tr>
                        )}
                        {caseStatus === '2' && (
                          <tr>
                            <td><p className="mb-0 fw-normal fw-semibold mb-0">Patient Situation</p></td>
                            <td>
                              <p className="mb-0 fw-normal">
                                <a
                                  className="text-primary"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleViewTransferPatientSituation();
                                  }}
                                  style={{ cursor: 'pointer' }}
                                >
                                  Click to view details
                                </a>
                              </p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Transfer Button */}
                  <div className="btn-panel pt-4">
                    {showTransferButton && (
                      <button
                        onClick={handleTransfer}
                        className="btn mb-1 waves-effect waves-light btn-rounded btn-warning"
                      >
                        Transfer
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Document Viewer Modal */}
            {showDocumentModal && (
              <div className="modal fade show d-block bg-black bg-opacity-50" tabIndex="-1">
                <div className="modal-dialog modal-lg">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Attachment</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowDocumentModal(false)}
                      ></button>
                    </div>
                    <div className="modal-body text-center">
                      {selectedDocument && renderDocument(selectedDocument)}
                    </div>
                    <div className="modal-footer">
                      <button
                        className="btn bg-danger-subtle text-danger"
                        onClick={() => setShowDocumentModal(false)}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Modals */}
            <TransferCaseModal
              show={showTransferModal}
              onHide={(success = false) => {
                setShowTransferModal(false);
                if (success) {
                  fetchCase(); // This refreshes the entire page data!
                }
              }}
              transferCaseData={{ caseId }}
            />

            {/* Regular Patient Situation Modal */}
            <PatientSituationModal
              ref={patientModalRef}
              isOpen={isPatientModalOpen}
              onClose={() => {
                setIsPatientModalOpen(false);
                setShowPatientModal(false);
              }}
              caseId={caseId}
            />

            {/* Transfer Patient Situation Modal */}
            <PatientSituationtransfercaseModal
              isOpen={showTransferPatientModal}
              onClose={() => setShowTransferPatientModal(false)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EMSClosedDetailTab;